/**
 * Partes de componente que só descem quando são usadas (SPEC 10.4): o painel aberto de um menu, o
 * conteúdo de um popover. O gatilho fica no bundle inicial; a parte vem num chunk próprio, pedida
 * no ócio depois da primeira pintura útil (`preloadWhenIdle`) ou, no pior caso, ao abrir.
 *
 * Diferente de `React.lazy` puro: quando o chunk já chegou, a parte renderiza direto, sem passar
 * pelo `<Suspense>` (o React segura a revelação de um fallback por ~300 ms e o 1º clique pareceria
 * lento). Enquanto não chegou, suspende normalmente; quem usa decide o fallback.
 */
import { type ComponentType, createElement, lazy, type ReactElement } from 'react';
import { whenPainted } from './afterPaint';

export interface LazyPart<P extends object> {
  /** O componente: direto se o chunk já chegou; senão, `lazy` (suspende até chegar). */
  Part: (props: P) => ReactElement;
  /** Baixa o chunk agora (idempotente). Rejeições não ficam em cache: a próxima chamada tenta de novo. */
  preload(): Promise<void>;
  /** Baixa o chunk no ócio, depois da primeira pintura útil (idempotente). */
  preloadWhenIdle(): void;
  /** O chunk já chegou (a parte renderiza sem suspender). */
  isLoaded(): boolean;
}

const idle = (cb: () => void): void => {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(cb, { timeout: 2000 });
  } else {
    setTimeout(cb, 200);
  }
};

/** Todas as partes criadas (para `preloadAllParts`, usado pelo setup dos testes). */
const registry = new Set<LazyPart<never>>();

export function lazyPart<P extends object>(loader: () => Promise<ComponentType<P>>): LazyPart<P> {
  let loaded: ComponentType<P> | null = null;
  let pending: Promise<ComponentType<P>> | null = null;
  let scheduled = false;
  const load = (): Promise<ComponentType<P>> => {
    pending ??= loader().then(
      (c) => {
        loaded = c;
        return c;
      },
      (err: unknown) => {
        pending = null;
        throw err;
      },
    );
    return pending;
  };
  const Lazy = lazy(() => load().then((c) => ({ default: c })));
  function Part(props: P): ReactElement {
    // Fora do React Compiler: ele cachearia a 1ª escolha (lazy) e reescreveria a tag de variável.
    'use no memo';
    return createElement((loaded ?? Lazy) as ComponentType<P>, props);
  }
  const part: LazyPart<P> = {
    Part,
    preload: () => load().then(() => undefined),
    preloadWhenIdle: () => {
      if (scheduled || loaded) return;
      scheduled = true;
      whenPainted(() => idle(() => void load().catch(() => {})));
    },
    isLoaded: () => loaded !== null,
  };
  registry.add(part as unknown as LazyPart<never>);
  return part;
}

/** Baixa todas as partes já registradas (testes: o painel abre no mesmo tick do clique). */
export function preloadAllParts(): Promise<void> {
  return Promise.all([...registry].map((p) => p.preload())).then(() => undefined);
}
